// Shared locale normalization — the SSOT for mapping external UI language codes
// (Bitrix24 / Uspacy `LANGUAGE_ID`, browser tags) onto our supported locales.
//
// This lived only in edge-api before, which let the embedded frontend diverge:
// the backend knew `ua → uk` / `la → es`, but the frontend's own mapper did not
// and degraded those codes to `en`, then posted `en` back over the correctly
// seeded org language. Keeping the alias map here means frontend and backend
// normalize identically. See i18n/types.ts `Language` for the supported set.
import type { Language } from './types';

/**
 * The language every fallback resolves to when nothing better is known.
 *
 * Every "we don't know the language" path — an org whose settings never got a
 * language, a project without `analysisLanguage`, a CRM that withheld its
 * `LANGUAGE_ID` — lands here. It used to be a `'uk'` literal repeated across
 * ~20 call sites in edge-api, workflows-api and the SPA, which meant an English
 * Bitrix24 install silently got Ukrainian analysis, Ukrainian emails and a
 * Ukrainian project language. Import this instead of writing the literal.
 */
export const DEFAULT_LANGUAGE: Language = 'en';

/**
 * External UI language code → supported {@link Language}.
 *
 * Note the two non-obvious aliases: Bitrix24 uses `ua` for Ukrainian (not the
 * ISO `uk`) and `la` for Latin-American Spanish (not `es`). Codes absent here
 * resolve to `undefined` so callers can apply their own fallback (usually `en`).
 */
export const BITRIX_LOCALE_ALIASES: Record<string, Language> = {
  en: 'en',
  ru: 'ru',
  ua: 'uk',
  uk: 'uk',
  la: 'es',
  es: 'es',
};

/**
 * Normalize an external UI language code onto a supported {@link Language}.
 *
 * Strips region/script suffixes (`uk_UA`, `es-419` → `uk`, `es`), lowercases,
 * and applies the Bitrix24 aliases. Returns `undefined` for unknown/empty
 * codes so the caller decides the fallback (canonically `en`).
 */
export function normalizeBitrixLocale(code?: string | null): Language | undefined {
  if (!code) return undefined;
  const base = code.toLowerCase().split(/[_-]/)[0] ?? '';
  return BITRIX_LOCALE_ALIASES[base];
}
